(() => {
  'use strict';
  const esc = (s) =>
    String(s ?? '').replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c],
    );
  if (!customElements.get('sf8-device-journey'))
    customElements.define(
      'sf8-device-journey',
      class extends HTMLElement {
        connectedCallback() {
          this.controller = new AbortController();
          this.config = JSON.parse(this.querySelector('[data-journey-config]').textContent);
          this.variants = this.config.variants;
          this.selection =
            this.config.selected?.options ||
            this.variants.find((v) => v.available)?.options ||
            this.variants[0]?.options ||
            [];
          const nav = this.querySelector('.sf8-sticky-steps');
          if (nav) {
            this.querySelectorAll('.sf8-step[id]').forEach((step) => {
              const b = nav.querySelector('[data-step-target="' + CSS.escape(step.id) + '"]');
              if (b) nav.append(b);
            });
          }
          const opts = { signal: this.controller.signal };
          this.addEventListener(
            'change',
            (e) => {
              if (e.target.dataset.journeyOption === undefined) return;
              this.selection[Number(e.target.dataset.journeyOption)] = e.target.value;
              this.settle(Number(e.target.dataset.journeyOption));
              this.update();
            },
            opts,
          );
          this.addEventListener(
            'click',
            (e) => {
              const photo = e.target.closest('[data-gallery-image]');
              if (photo) {
                const img = this.querySelector('.sf8-intro-photo [data-journey-image]');
                img.removeAttribute('srcset');
                img.src = photo.dataset.galleryImage;
                img.alt = photo.dataset.galleryAlt;
                img.hidden = false;
                this.querySelector('.sf8-intro-photo [data-journey-placeholder]').hidden = true;
                this.querySelectorAll('[data-gallery-image]').forEach((b) =>
                  b.setAttribute('aria-pressed', String(b === photo)),
                );
              }
              const b = e.target.closest('[data-step-target], [data-step-first]');
              if (b?.hasAttribute('data-step-first')) {
                this.querySelector('.sf8-step[id]')?.scrollIntoView({
                  behavior:
                    e.detail === 0 || matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth',
                  block: 'start',
                });
                return;
              }
              if (b)
                this.querySelector('#' + CSS.escape(b.dataset.stepTarget))?.scrollIntoView({
                  behavior:
                    e.detail === 0 || matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth',
                  block: 'start',
                });
            },
            opts,
          );
          // Steps in page order (Condition → Colour → Storage): a choice only depends on the steps above it.
          this.order = [];
          this.querySelectorAll('[data-journey-option]').forEach((input) => {
            const i = Number(input.dataset.journeyOption);
            if (!this.order.includes(i)) this.order.push(i);
          });
          this.settle(-1);
          this.update();
        }
        // Is there a variant in stock with this value, given the choices in the steps above it?
        status(index, value) {
          const pos = this.order.indexOf(index);
          const before = this.order.slice(0, pos);
          const fits = this.variants.filter(
            (v) => v.options[index] === value && before.every((j) => v.options[j] === this.selection[j]),
          );
          return fits.some((v) => v.available) ? 'ok' : fits.length ? 'soldout' : 'none';
        }
        // After a change, steps below it switch to the first choice that is in stock (if the current one is not).
        settle(changed) {
          const start = changed < 0 ? 0 : this.order.indexOf(changed) + 1;
          for (const index of this.order.slice(start)) {
            if (this.status(index, this.selection[index]) === 'ok') continue;
            const values = [...new Set(this.variants.map((v) => v.options[index]))];
            const next = values.find((value) => this.status(index, value) === 'ok');
            if (next !== undefined) this.selection[index] = next;
          }
        }
        markOptions() {
          this.querySelectorAll('[data-journey-option]').forEach((input) => {
            const index = Number(input.dataset.journeyOption);
            const state = this.status(index, input.value);
            const label = input.closest('label');
            input.checked = this.selection[index] === input.value;
            input.disabled = state !== 'ok';
            label?.classList.toggle('is-unavailable', state !== 'ok');
            const note = label?.querySelector('[data-option-note]');
            if (note) note.textContent = state === 'soldout' ? this.config.soldOut : state === 'none' ? this.config.unavailable : '';
          });
        }
        update() {
          this.markOptions();
          const v = this.variants.find((v) => v.options.every((o, i) => o === this.selection[i]));
          this.querySelectorAll('[data-selected-option]').forEach(
            (el) => (el.textContent = this.selection[Number(el.dataset.selectedOption)] || ''),
          );
          this.querySelectorAll('[data-journey-price]').forEach(
            (el) => (el.textContent = v ? v.formattedPrice : this.config.unavailable),
          );
          this.querySelectorAll('[data-journey-image]').forEach((img) => {
            img.hidden = !v?.image;
            if (v?.image) img.src = v.image;
            if (v?.srcset) img.srcset = v.srcset;
            else img.removeAttribute('srcset');
            img.alt = this.config.title + ' · ' + this.selection.join(' · ');
          });
          this.querySelectorAll('[data-journey-placeholder]').forEach((node) => {
            node.hidden = Boolean(v?.image);
          });
          this.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
            const input = form.querySelector('[name=id]');
            if (input) input.value = v?.id || '';
            const button = form.querySelector('[type=submit]');
            if (button) {
              button.disabled = !v?.available;
              const label = button.querySelector('[data-journey-add-label]');
              if (label)
                label.textContent = !v ? this.config.unavailable : v.available ? this.config.add : this.config.soldOut;
              button.removeAttribute('aria-disabled');
            }
          });
          const reference = Boolean(v?.referenceNote && v?.referencePrice);
          this.querySelectorAll('[data-journey-reference]').forEach((el) => (el.hidden = !reference));
          this.querySelectorAll('[data-journey-reference-price]').forEach(
            (el) => (el.textContent = v?.referencePrice || ''),
          );
          this.querySelectorAll('[data-journey-reference-note]').forEach(
            (el) => (el.textContent = v?.referenceNote || ''),
          );
          // "Save €… compared to new": the variant's compare-at price is its new price.
          this.querySelectorAll('[data-journey-save]').forEach((el) => (el.hidden = !v?.save));
          this.querySelectorAll('[data-journey-save-value]').forEach((el) => (el.textContent = v?.save || ''));
          this.querySelectorAll('[data-journey-new-price]').forEach((el) => (el.textContent = v?.newPrice || ''));
          this.querySelectorAll('[data-grade-visual]').forEach((el) => {
            const value = this.selection[Number(el.dataset.gradeVisual)];
            const block = this.querySelector('[data-condition-name="' + CSS.escape(value || '') + '"]');
            const img = el.querySelector('img');
            if (img) {
              const custom = block?.dataset.conditionImage;
              el.classList.toggle('has-custom', Boolean(custom));
              img.src = custom || img.dataset.conditionFallback;
            }
            el.dataset.grade = block?.dataset.conditionGrade || 'excellent';
            const caption = this.querySelector('[data-condition-caption]');
            if (caption) caption.textContent = block?.dataset.conditionDescription || '';
          });
          this.querySelectorAll('[data-storage-value]').forEach(
            (el) => (el.textContent = this.selection[Number(el.dataset.storageValue)] || ''),
          );
          this.querySelectorAll('[data-journey-summary]').forEach(
            (summary) => (summary.innerHTML = this.selection.map((s) => '<span>' + esc(s) + '</span>').join('')),
          );
          if (v) {
            const url = new URL(location.href);
            url.searchParams.set('variant', v.id);
            history.replaceState({}, '', url);
          }
          this.querySelectorAll('[data-journey-status]').forEach(
            (s) => (s.textContent = v ? '' : this.config.unavailable),
          );
        }
        disconnectedCallback() {
          this.controller?.abort();
        }
      },
    );
})();
